import {Expressions} from '@packages/systems/wfdl';
import {EText, type Expression} from '@packages/systems/wfdl-ast';
import {$, at} from '@packages/utilities/wfdl-helpers';
import {getOverlayTargetReferenceParts} from '../shared/expression';
import {isModalAction, type ModalAction} from './constants';

const MODAL_TARGET_CONTEXT_KEY = 'modalTarget';

export type ModalTargetReference = {
  componentKey: string;
  targetId: string;
  action: ModalAction;
};

/**
 * Reads a `context.modalTarget(componentKey)(targetId)(action)` call. Returns
 * null for any other expression, including a Modal call with a missing or
 * unknown action; use `getModalTargetReferenceForRead` to accept those.
 */
export const getModalTargetReference = (
  expression: Expression
): ModalTargetReference | null => {
  const reference = getOverlayTargetReferenceParts(
    expression,
    MODAL_TARGET_CONTEXT_KEY
  );
  if (!reference || !isModalAction(reference.action)) return null;
  return {...reference, action: reference.action};
};

/** Reads durable Modal targets while normalizing missing or unknown actions. */
export const getModalTargetReferenceForRead = (
  expression: Expression
): ModalTargetReference | null => {
  const reference = getOverlayTargetReferenceParts(
    expression,
    MODAL_TARGET_CONTEXT_KEY
  );
  if (!reference) return null;
  return {
    ...reference,
    action: isModalAction(reference.action) ? reference.action : 'show',
  };
};

export const createModalTargetExpression = ({
  componentKey,
  targetId,
  action,
}: ModalTargetReference): Expression =>
  Expressions._call(at($('context'), [MODAL_TARGET_CONTEXT_KEY]), [
    EText(componentKey),
    EText(targetId),
    EText(action),
  ]);
